import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = 'https://social-auto-r8we.onrender.com/api/telegram';

export const addBot = createAsyncThunk(
    'telegram/addBot',
    async (botToken, { getState, rejectWithValue }) => {
        try {
            const token = getState().auth.token;
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const { data } = await axios.post(`${API_URL}/add-bot`, { botToken }, config);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to add bot');
        }
    }
);

export const getBots = createAsyncThunk(
    'telegram/getBots',
    async (_, { getState, rejectWithValue }) => {
        try {
            const token = getState().auth.token;
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const { data } = await axios.get(`${API_URL}/bots`, config);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to get bots');
        }
    }
);

export const sendMessage = createAsyncThunk(
    'telegram/sendMessage',
    async ({ accountId, chatId, text }, { getState, rejectWithValue }) => {
        try {
            const token = getState().auth.token;
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const { data } = await axios.post(`${API_URL}/send-message`, { accountId, chatId, text }, config);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to send message');
        }
    }
);

export const sendPhoto = createAsyncThunk(
    'telegram/sendPhoto',
    async ({ accountId, chatId, photo, caption }, { getState, rejectWithValue }) => {
        try {
            const token = getState().auth.token;
            const formData = new FormData();
            formData.append('accountId', accountId);
            formData.append('chatId', chatId);
            formData.append('photo', photo);
            if (caption) formData.append('caption', caption);

            const config = {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            };
            const { data } = await axios.post(`${API_URL}/send-photo`, formData, config);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to send photo');
        }
    }
);

export const sendVideo = createAsyncThunk(
    'telegram/sendVideo',
    async ({ accountId, chatId, video, caption }, { getState, rejectWithValue }) => {
        try {
            const token = getState().auth.token;
            const formData = new FormData();
            formData.append('accountId', accountId);
            formData.append('chatId', chatId);
            formData.append('video', video);
            if (caption) formData.append('caption', caption);

            const config = {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            };
            const { data } = await axios.post(`${API_URL}/send-video`, formData, config);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to send video');
        }
    }
);

export const removeBot = createAsyncThunk(
    'telegram/removeBot',
    async (accountId, { getState, rejectWithValue }) => {
        try {
            const token = getState().auth.token;
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const { data } = await axios.delete(`${API_URL}/bot/${accountId}`, config);
            return { accountId, ...data };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to remove bot');
        }
    }
);

export const getChat = createAsyncThunk(
    'telegram/getChat',
    async ({ accountId, chatId }, { getState, rejectWithValue }) => {
        try {
            const token = getState().auth.token;
            const config = {
                headers: { Authorization: `Bearer ${token}` },
                params: { accountId, chatId }
            };
            const { data } = await axios.get(`${API_URL}/chat`, config);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to get chat info');
        }
    }
);

const telegramSlice = createSlice({
    name: 'telegram',
    initialState: {
        bots: [],
        selectedBot: null,
        loading: false,
        error: null,
    },
    reducers: {
        selectBot: (state, action) => {
            state.selectedBot = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(addBot.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addBot.fulfilled, (state, action) => {
                state.loading = false;
                state.bots.push(action.payload.account);
                state.selectedBot = action.payload.account._id;
            })
            .addCase(addBot.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(getBots.fulfilled, (state, action) => {
                state.bots = action.payload.data || [];
                if (state.bots.length > 0 && !state.selectedBot) {
                    state.selectedBot = state.bots[0]._id;
                }
            })
            .addCase(sendMessage.pending, (state) => {
                state.loading = true;
            })
            .addCase(sendMessage.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(sendMessage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(removeBot.fulfilled, (state, action) => {
                state.loading = false;
                state.bots = state.bots.filter(b => b._id !== action.payload.accountId);
                if (state.selectedBot === action.payload.accountId) {
                    state.selectedBot = state.bots.length > 0 ? state.bots[0]._id : null;
                }
            })
            .addCase(sendVideo.pending, (state) => { state.loading = true; })
            .addCase(sendVideo.fulfilled, (state) => { state.loading = false; })
            .addCase(sendVideo.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const getUpdates = createAsyncThunk(
    'telegram/getUpdates',
    async (accountId, { getState, rejectWithValue }) => {
        try {
            const token = getState().auth.token;
            const config = {
                headers: { Authorization: `Bearer ${token}` },
                params: { accountId }
            };
            const { data } = await axios.get(`${API_URL}/updates`, config);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to get updates');
        }
    }
);

export const { selectBot } = telegramSlice.actions;
export default telegramSlice.reducer;
